import React from "react";
import { CarouselProps } from "./types";
import { CAROUSEL_SCROLL_OFFSET, CAROUSEL_SCROLL_SPEED_MULTIPLIER } from "shared/utils/styleConstants";

function getInitialScrollLeft({
  scrollWidth,
  clientWidth,
  activeIndex,
  itemCount
}: {
  scrollWidth: number;
  clientWidth: number;
  activeIndex: number;
  itemCount: number;
}) {
  const itemWidth = scrollWidth / itemCount;
  const itemInView = clientWidth / itemWidth;
  return (activeIndex - itemInView / 2) * itemWidth + CAROUSEL_SCROLL_OFFSET;
}

export default function useCarouselService(props: CarouselProps) {
  const carouselRef = React.useRef<any>();
  const [scrollable, setScrollable] = React.useState<boolean>(true);
  const [showNextButton, setShowNextButton] = React.useState<boolean>(true);
  const [showBackButton, setShowBackButton] = React.useState<boolean>(false);

  const scrollBySpeed = React.useMemo(
    () => props.scrollBySpeed || window.innerWidth * CAROUSEL_SCROLL_SPEED_MULTIPLIER,
    [props.scrollBySpeed]
  );

  const showScrollButtons = React.useMemo(
    () => props.keepSpaceForScrollButtons || scrollable,
    [props.keepSpaceForScrollButtons, scrollable]
  );

  const scrollButtonVisibilityHidden = React.useMemo(
    () => props.keepSpaceForScrollButtons && !scrollable,
    [props.keepSpaceForScrollButtons, scrollable]
  );

  React.useEffect(() => {
    //setting initial scroll position
    if (carouselRef.current) {
      const { clientWidth, scrollWidth } = carouselRef.current;
      if (scrollWidth === clientWidth) {
        //scroll not needed, hiding buttons
        setScrollable(false);
      } else if (props.activeIndex > 0) {
        const scrollLeft = getInitialScrollLeft({
          scrollWidth,
          clientWidth,
          activeIndex: props.activeIndex,
          itemCount: props.children.length
        });
        if (scrollLeft > 0) {
          setShowBackButton(true);
          if (scrollLeft === scrollWidth) {
            setShowNextButton(false);
          }
          carouselRef.current?.scrollTo({
            left: scrollLeft,
            behavior: "instant"
          });
        }
      }
    }
  }, [carouselRef, props.activeIndex, props.children.length]);

  const handleBack = React.useCallback(() => {
    carouselRef.current?.scrollBy({ left: -scrollBySpeed });
  }, [scrollBySpeed]);

  const handleNext = React.useCallback(() => {
    carouselRef.current?.scrollBy({ left: scrollBySpeed });
  }, [scrollBySpeed]);

  const onScroll = React.useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      if (scrollLeft <= CAROUSEL_SCROLL_OFFSET) {
        setShowBackButton(false);
      } else if (!showBackButton) {
        setShowBackButton(true);
      }
      if (scrollLeft + clientWidth + CAROUSEL_SCROLL_OFFSET >= scrollWidth) {
        setShowNextButton(false);
      } else if (!showNextButton) {
        setShowNextButton(true);
      }
    }
  }, [showBackButton, showNextButton]);

  return {
    scrollable,
    onScroll,
    carouselRef,
    showBackButton,
    showNextButton,
    handleBack,
    handleNext,
    showScrollButtons,
    scrollButtonVisibilityHidden
  };
}
